import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Input, Empty, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { Global } from '../../constants';
import { DataService } from '../../services/http-interceptor.service';

type Resultado = {
  id: string;
  name: string;
  stage: string;
  isDraft?: boolean;
  dateInit?: string;
  client?: { name?: string };
};

const LIMITE = 8;
const ESPERA_MS = 300;

/**
 * Búsqueda global de la barra superior (PRD §4).
 *
 * Se abre con ⌘K / Ctrl+K y consulta el mismo endpoint del listado, así que
 * respeta el alcance por rol y encuentra también borradores.
 */
const GlobalSearch: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<any>(null);

  const [abierto, setAbierto] = useState(false);
  const [termino, setTermino] = useState('');
  const [resultados, setResultados] = useState<Resultado[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [seleccion, setSeleccion] = useState(0);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAbierto((v) => !v);
      }
      if (e.key === 'Escape') setAbierto(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!termino.trim()) {
      setResultados([]);
      return undefined;
    }
    const debounce = setTimeout(async () => {
      try {
        setBuscando(true);
        const response = await DataService.get(
          `documents?limit=${LIMITE}&page=1&search=${encodeURIComponent(termino.trim())}`
        );
        setResultados(response?.data?.results || []);
        setSeleccion(0);
      } catch (e) {
        setResultados([]);
      } finally {
        setBuscando(false);
      }
    }, ESPERA_MS);

    return () => clearTimeout(debounce);
  }, [termino]);

  const abrir = useCallback(
    (r: Resultado) => {
      setAbierto(false);
      setTermino('');
      if (r.isDraft) navigate(`${Global.APP_URLS.DOCUMENT_CREATOR_V2}?draft=${r.id}`);
      else navigate(`/admin/document/${r.id}`);
    },
    [navigate]
  );

  const onKeyDownInput = (e: any) => {
    if (!resultados.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSeleccion((i) => (i + 1) % resultados.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSeleccion((i) => (i - 1 + resultados.length) % resultados.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      abrir(resultados[seleccion]);
    }
  };

  return (
    <>
      <button type="button" className="global-search__trigger" onClick={() => setAbierto(true)}>
        <SearchOutlined />
        <span>{t('common.search_placeholder')}</span>
        <kbd>⌘K</kbd>
      </button>

      <Modal
        open={abierto}
        onCancel={() => setAbierto(false)}
        footer={null}
        closable={false}
        destroyOnClose
        width={620}
        styles={{ body: { padding: 0 } }}
        afterOpenChange={(open) => open && inputRef.current?.focus()}
      >
        <Input
          ref={inputRef}
          size="large"
          bordered={false}
          prefix={<SearchOutlined style={{ color: 'var(--color-neutral-400)' }} />}
          placeholder={t('common.search_placeholder')}
          value={termino}
          onChange={(e) => setTermino(e.target.value)}
          onKeyDown={onKeyDownInput}
        />

        <div className="global-search__results">
          {buscando && (
            <div className="global-search__loading">
              <Spin size="small" />
            </div>
          )}

          {!buscando && termino.trim() && !resultados.length && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('common.search_empty')} />
          )}

          {!buscando &&
            resultados.map((r, i) => {
              const etapa = Global.DOCUMENT_STAGES.find((s: any) => s.key === r.stage);
              return (
                <button
                  type="button"
                  key={r.id}
                  className={`global-search__item${i === seleccion ? ' is-active' : ''}`}
                  onMouseEnter={() => setSeleccion(i)}
                  onClick={() => abrir(r)}
                >
                  <span className="global-search__name">{r.name}</span>
                  <span className="global-search__meta">
                    {r.client?.name || '—'}
                    {r.dateInit ? ` · ${moment(r.dateInit).format('DD/MM/YYYY')}` : ''}
                  </span>
                  {etapa && (
                    <span
                      className="stage-pill"
                      style={{ backgroundColor: etapa.bg, color: etapa.fg }}
                    >
                      {t(`documents.stages.${etapa.key}`)}
                    </span>
                  )}
                </button>
              );
            })}
        </div>
      </Modal>
    </>
  );
};

export default GlobalSearch;
